import { useCallback, useRef, useState } from "react";
import axios from "../api/axiosConfig";
import useRealtimeRefresh from "./useRealtimeRefresh";

// In-app notifications for the signed-in user. The unread count is pushed by
// the server (see useRealtimeRefresh) with a slow fallback poll that pauses
// while the tab is hidden; the list is loaded only when the dropdown opens.
export default function useNotifications(enabled = true, pollMs = 120000) {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const lastUnread = useRef(0);

  const loadCount = useCallback(async () => {
    try {
      const res = await axios.get("/notifications/unread-count");
      const n = Number(res.data?.unread) || 0;
      lastUnread.current = n;
      setUnread(n);
      return n;
    } catch {
      // The badge is best-effort; stay quiet if the server is unreachable.
      return lastUnread.current;
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get("/notifications", { params: { limit: 20 } });
      setItems(res.data?.data || []);
      const n = Number(res.data?.unread) || 0;
      lastUnread.current = n;
      setUnread(n);
    } catch (err) {
      setError(err?.error || err?.message || "Could not load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  // Pushed by the server when something new arrives; the poll is only a slow
  // fallback and pauses while the tab is hidden.
  useRealtimeRefresh("notifications", loadCount, { enabled, fallbackMs: pollMs });

  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.map((n) => (n._id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await axios.post(`/notifications/${id}/read`);
    } catch {
      loadList();
    }
  }, [loadList]);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })));
    setUnread(0);
    try {
      await axios.post("/notifications/read-all");
    } catch {
      loadList();
    }
  }, [loadList]);

  const remove = useCallback(async (id) => {
    const was = items.find((n) => n._id === id);
    setItems((prev) => prev.filter((n) => n._id !== id));
    if (was && !was.readAt) setUnread((u) => Math.max(0, u - 1));
    try {
      await axios.delete(`/notifications/${id}`);
    } catch {
      loadList();
    }
  }, [items, loadList]);

  return { unread, items, loading, error, loadList, markRead, markAllRead, remove };
}
